import { defineStore } from 'pinia'
import ProjectService from '@/services/ProjectService'

const STORAGE_KEY = 'selectedProjectId'

const extractItems = response => {
  if (Array.isArray(response)) return response
  if (Array.isArray(response?.data?.items)) return response.data.items
  if (Array.isArray(response?.data)) return response.data
  if (Array.isArray(response?.items)) return response.items
  return []
}

const extractItem = response => response?.data?.item || response?.data || response?.item || response

/**
 * ProjectStore — manages Projects.
 *
 * A Project is the top-level organisational unit that encompasses
 * assets, devices, team members, integrations and automations.
 *
 * The `activeProject` concept acts as a global context filter:
 *   - Its ID is persisted to localStorage
 *   - Other stores/components watch `activeProject` to re-fetch data
 *   - API scoping must be explicit via `project_id` query params or payload fields
 */
export const useProjectStore = defineStore('ProjectStore', {
  state: () => ({
    /** All projects for the org. */
    projects: [],
    /** The currently-selected project for global filtering (null = "All"). */
    activeProject: null,
    /** Full detail of a single project being viewed/edited. */
    selectedProject: null,
    stats: null,
    loading: false,
    error: null,
  }),

  getters: {
    getProjects: state => state.projects,
    totalProjects: state => state.projects.length,

    /** Find a project by its _id. */
    getProjectById: state => id =>
      state.projects.find(p => p._id === id || p.id === id),

    /** Is a global project filter currently active? */
    hasActiveProject: state => !!state.activeProject,

    /** The active project's _id (or null). Use explicitly as `project_id` when filtering API calls. */
    activeProjectId: state => state.activeProject?._id || state.activeProject?.id || null,

    /** Projects filtered by status. */
    projectsByStatus: state => status =>
      state.projects.filter(p => p.status === status),

    /** Projects filtered by type. */
    projectsByType: state => type =>
      state.projects.filter(p => p.type === type),

    /** Count of active projects. */
    activeProjectsCount: state =>
      state.projects.filter(p => p.status === 'active' || p.is_active !== false).length,
  },

  actions: {
    /* ─── CRUD ──────────────────────────────────────────────── */

    async fetchProjects(params = {}) {
      this.loading = true
      this.error = null
      try {
        const data = await ProjectService.getProjects(params)

        this.projects = extractItems(data)

        // Restore persisted active project
        const savedId = localStorage.getItem(STORAGE_KEY)
        if (savedId && !this.activeProject) {
          const match = this.projects.find(p => (p._id || p.id) === savedId)
          if (match) this.activeProject = match
        }

        return this.projects
      } catch (err) {
        this.error = err.message || 'Error fetching projects'
        console.error('[ProjectStore] fetchProjects:', err)
        return []
      } finally {
        this.loading = false
      }
    },

    async fetchProject(projectId) {
      this.loading = true
      try {
        const data = await ProjectService.getProject(projectId)
        this.selectedProject = extractItem(data)
        return this.selectedProject
      } catch (err) {
        this.error = err.message || 'Error fetching project'
        console.error('[ProjectStore] fetchProject:', err)
        return null
      } finally {
        this.loading = false
      }
    },

    async fetchProjectStats(params = {}) {
      try {
        const data = await ProjectService.getProjectStats(params)
        this.stats = data?.data || data
        return this.stats
      } catch (err) {
        console.error('[ProjectStore] fetchProjectStats:', err)
      }
    },

    async createProject(payload) {
      const data = await ProjectService.createProject(payload)
      const project = extractItem(data)
      this.projects.push(project)
      return project
    },

    async updateProject(projectId, payload) {
      const data = await ProjectService.updateProject(projectId, payload)
      const updated = extractItem(data)
      const idx = this.projects.findIndex(p => (p._id || p.id) === projectId)
      if (idx !== -1) this.projects.splice(idx, 1, { ...this.projects[idx], ...payload, ...updated })
      if (this.activeProject && (this.activeProject._id || this.activeProject.id) === projectId) {
        this.activeProject = this.projects[idx] || updated
      }
      if (this.selectedProject && (this.selectedProject._id || this.selectedProject.id) === projectId) {
        this.selectedProject = { ...this.selectedProject, ...payload, ...updated }
      }
      return updated
    },

    async archiveProject(projectId) {
      await ProjectService.archiveProject(projectId)
      const idx = this.projects.findIndex(p => (p._id || p.id) === projectId)
      if (idx !== -1) this.projects[idx] = { ...this.projects[idx], status: 'archived', is_active: false }
      if (this.activeProjectId === projectId) this.clearProjectFilter()
    },

    async deleteProject(projectId) {
      await ProjectService.deleteProject(projectId)
      this.projects = this.projects.filter(p => (p._id || p.id) !== projectId)
      if (this.activeProjectId === projectId) this.clearProjectFilter()
    },

    /* ─── Global Context Switcher ───────────────────────────── */

    /**
     * Set the active project for global filtering.
     * Persists to localStorage so it survives page refresh.
     */
    selectProject(projectOrId) {
      if (typeof projectOrId === 'string') {
        this.activeProject = this.getProjectById(projectOrId) || { _id: projectOrId }
      } else {
        this.activeProject = projectOrId
      }
      const id = this.activeProject?._id || this.activeProject?.id
      if (id) localStorage.setItem(STORAGE_KEY, id)
    },

    /**
     * Clear the global project filter (show all projects).
     */
    clearProjectFilter() {
      this.activeProject = null
      localStorage.removeItem(STORAGE_KEY)
    },

    /* ─── Legacy compat ─────────────────────────────────────── */

    // Backward-compat aliases used during migration
    get groups() { return this.projects },
    get activeGroup() { return this.activeProject },
    get activeGroupId() { return this.activeProjectId },
    get hasActiveGroup() { return this.hasActiveProject },
    getGroupById(id) { return this.getProjectById(id) },
    fetchGroups(params) { return this.fetchProjects(params) },
    fetchGroup(id) { return this.fetchProject(id) },
    selectGroup(g) { return this.selectProject(g) },
    clearGroupFilter() { return this.clearProjectFilter() },
    createGroup(p) { return this.createProject(p) },
    updateGroup(id, p) { return this.updateProject(id, p) },
    archiveGroup(id) { return this.archiveProject(id) },
    deleteGroup(id) { return this.deleteProject(id) },

    /** @deprecated Use selectProject() */
    setProject(project) {
      this.selectedProject = project
    },
  },
})